import { createTheme } from '@mui/material/styles';

export const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'light' ? '#4f46e5' : '#38bdf8',      // Indigo / Cosmic blue
      light: mode === 'light' ? '#818cf8' : '#7dd3fc',
      dark: mode === 'light' ? '#3730a3' : '#0284c7',
      contrastText: '#ffffff',
    },
    secondary: {
      main: mode === 'light' ? '#06b6d4' : '#34d399',       // Cyan / Emerald
      light: mode === 'light' ? '#22d3ee' : '#6ee7b7',
      dark: mode === 'light' ? '#0891b2' : '#059669',
      contrastText: '#ffffff',
    },
    // Amber accent — used sparingly for CTAs only
    accent: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
      subtle: mode === 'light' ? 'rgba(0, 0, 0, 0.03)' : 'rgba(245, 158, 11, 0.12)',
      glow: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(245, 158, 11, 0.25)',
    },
    warning: {
      main: mode === 'light' ? '#f59e0b' : '#fbbf24',
      light: mode === 'light' ? '#fbbf24' : '#fcd34d',
      dark: mode === 'light' ? '#d97706' : '#d97706',
    },
    background: {
      default: mode === 'light'
        ? '#FAFAF9'                                          // Warm stone-50 (was cold #f0f2ff)
        : 'linear-gradient(45deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
      paper: mode === 'light' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(15, 23, 42, 0.95)',
      glass: mode === 'light'
        ? 'rgba(255, 255, 255, 0.55)'
        : 'rgba(30, 41, 59, 0.3)',
      sectionA: mode === 'light' ? 'rgba(255, 255, 255, 0.45)' : undefined,
      sectionB: mode === 'light' ? 'rgba(250, 250, 249, 0.55)' : undefined,
    },
    text: {
      primary: mode === 'light' ? '#0f172a' : '#f1f5f9',
      secondary: mode === 'light' ? '#475569' : '#cbd5e1',
      accent: mode === 'light' ? '#0f172a' : '#F59E0B',
    },
    deepSpace: {
      darkest: '#020617',
      dark: '#0f172a',
      medium: '#1e293b',
      light: '#334155',
      accent: '#475569',
    },
    cosmic: {
      blue: '#38bdf8',
      cyan: '#22d3ee',
      emerald: '#10b981',
      violet: '#8b5cf6',
      pink: '#ec4899',
      amber: '#F59E0B',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontFamily: '"Space Grotesk", "Inter", "Helvetica Neue", sans-serif',
      fontWeight: 700,
      fontSize: '3.5rem',
      letterSpacing: '-0.03em',
    },
    h2: {
      fontFamily: '"Space Grotesk", "Inter", "Helvetica Neue", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: '"DM Sans", "Inter", "Helvetica Neue", sans-serif',
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '-0.015em',
    },
    h4: {
      fontFamily: '"DM Sans", "Inter", "Helvetica Neue", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: '"DM Sans", "Inter", "Helvetica Neue", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontFamily: '"DM Sans", "Inter", "Helvetica Neue", sans-serif',
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    body1: {
      fontFamily: '"Inter", "Helvetica Neue", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"DM Sans", "Inter", "Helvetica Neue", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          padding: '12px 28px',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        containedPrimary: {
          background: mode === 'light'
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
          color: '#ffffff',
          boxShadow: mode === 'light'
            ? '0 8px 25px rgba(15, 23, 42, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)'
            : '0 8px 32px rgba(56, 189, 248, 0.35)',
          '&:hover': {
            background: mode === 'light'
              ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
              : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            transform: 'translateY(-2px)',
            boxShadow: mode === 'light'
              ? '0 14px 35px rgba(15, 23, 42, 0.25), 0 4px 12px rgba(0, 0, 0, 0.12)'
              : '0 12px 48px rgba(56, 189, 248, 0.45)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          background: mode === 'light' ? 'rgba(255, 255, 255, 0.5)' : undefined,
          borderColor: mode === 'light' ? 'rgba(79, 70, 229, 0.25)' : 'rgba(56, 189, 248, 0.5)',
          color: mode === 'light' ? '#4f46e5' : '#38bdf8',
          '&:hover': {
            borderWidth: '1.5px',
            transform: 'translateY(-2px)',
            borderColor: mode === 'light' ? 'rgba(79, 70, 229, 0.5)' : 'rgba(16, 185, 129, 0.7)',
            backgroundColor: mode === 'light' ? 'rgba(79, 70, 229, 0.06)' : 'rgba(30, 41, 59, 0.3)',
            boxShadow: mode === 'light' ? '0 8px 24px rgba(79, 70, 229, 0.12)' : undefined,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: mode === 'light'
            ? '1px solid rgba(0, 0, 0, 0.06)'              // Defined border (was white-on-white)
            : '1px solid rgba(56, 189, 248, 0.2)',
          boxShadow: mode === 'light'
            ? '0 8px 32px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0,0,0,0.03)'
            : '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 20px rgba(56, 189, 248, 0.1)',
          backgroundColor: mode === 'light'
            ? 'rgba(255, 255, 255, 0.65)'                   // Slightly more opaque for contrast
            : 'rgba(15, 23, 42, 0.95)',
        },
      },
    },
  },
});

// Export default theme (light mode)
const defaultTheme = createAppTheme('light');
export default defaultTheme;